import React, { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { useGameSearch } from "hooks/useGameSearch"

import { CHALLENGE_FORM_FIELDS } from "../../constants/challenges"
import { CreateChallengeInput, GameChallenge, UpdateChallengeInput } from "../../services/challenge/type"
import { GameInterface } from "../../services/game/type"
import { formatFiatCurrency } from "../../util/formatter"
import { SaveButton } from "../buttons/SaveButton"
import { InputField } from "../inputs/InputField"

type ChallengeFormData = {
  id?: string
  game_id: string
  description: string
  min_bet_amount_usd: string
  required_multiplier: string
  prize_amount_usd: string
  end_at: string
  is_active: boolean
}

const EMPTY_FORM: ChallengeFormData = {
  game_id: "",
  description: "",
  min_bet_amount_usd: "",
  required_multiplier: "",
  prize_amount_usd: "",
  end_at: "",
  is_active: false
}

const challengeToForm = (challenge?: GameChallenge): ChallengeFormData => {
  if (!challenge) return EMPTY_FORM
  return {
    id: challenge.id,
    game_id: challenge.game_id,
    description: challenge.description ?? "",
    min_bet_amount_usd: String(challenge.min_bet_amount_usd),
    required_multiplier: String(challenge.required_multiplier),
    prize_amount_usd: String(challenge.prize_amount_usd),
    end_at: challenge.end_at ? challenge.end_at.slice(0, 10) : "",
    is_active: challenge.is_active
  }
}

interface ChallengeFormProps {
  initial?: GameChallenge
  onSubmit: (data: CreateChallengeInput | UpdateChallengeInput, id?: string) => void
  onCancel: () => void
  loading?: boolean
  title: string
}

export function ChallengeForm({ initial, onSubmit, onCancel, loading, title }: ChallengeFormProps) {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [form, setForm] = useState<ChallengeFormData>(() => challengeToForm(initial))
  const [selectedGame, setSelectedGame] = useState<GameInterface | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)

  const { query, setQuery, results, loading: searching, clear } = useGameSearch({ pageSize: 20 })

  useEffect(() => {
    if (results.length > 0) setSearchOpen(true)
  }, [results])

  useEffect(() => {
    setForm(challengeToForm(initial))
    setSelectedGame(null)
  }, [initial])

  const handleSelectGame = useCallback(
    (g: GameInterface) => {
      setForm((prev) => ({ ...prev, game_id: g.identifier }))
      setSelectedGame(g)
      clear()
      setSearchOpen(false)
    },
    [clear]
  )

  const handleClearGame = useCallback(() => {
    setForm((prev) => ({ ...prev, game_id: "" }))
    setSelectedGame(null)
  }, [])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }, [])

  const handleCheckbox = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, is_active: e.target.checked }))
  }, [])

  const isValid = useMemo(() => {
    return Boolean(form.game_id && form.min_bet_amount_usd && form.required_multiplier && form.prize_amount_usd)
  }, [form.game_id, form.min_bet_amount_usd, form.required_multiplier, form.prize_amount_usd])

  const handleSave = useCallback(() => {
    if (!isValid) return

    const payload: CreateChallengeInput = {
      game_id: form.game_id,
      description: form.description || undefined,
      min_bet_amount_usd: Number(form.min_bet_amount_usd),
      required_multiplier: Number(form.required_multiplier),
      prize_amount_usd: Number(form.prize_amount_usd),
      is_active: form.is_active,
      end_at: form.end_at ? new Date(form.end_at) : undefined
    }

    onSubmit(payload, form.id)
  }, [isValid, form, onSubmit])

  const inputFields = useMemo(() => {
    return CHALLENGE_FORM_FIELDS.map((config) => ({
      ...config,
      title: t(config.titleKey),
      placeholder: t(config.placeholderKey),
      value: form[config.name as keyof ChallengeFormData] as string,
      altLabelOne: config.showAltLabel ? formatFiatCurrency(Number(form[config.name as keyof ChallengeFormData]), "$0.00") : undefined
    }))
  }, [form, t])

  // Based on edit or adding new - resolve game in challenge to show game title and image. For new challenge, game is selected via search input. For edit, game is resolved from initial prop
  const resolvedGame = useMemo(() => {
    if (selectedGame) return selectedGame
    if (initial?.game) return initial.game
    return null
  }, [selectedGame, initial])

  return (
    <div className="flex flex-col gap-4 p-6 min-w-[400px] bg-base-100 rounded-lg">
      <h3 className="text-lg font-bold">{title}</h3>

      <div className="w-full form-control">
        <span className="label-text mb-1">{t("challenges.form.gameId")}</span>
        {form.game_id ? (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#0C0C1A]">
            {(resolvedGame?.image_vertical || resolvedGame?.image) && (
              <img
                src={resolvedGame.image_vertical || resolvedGame.image}
                alt={resolvedGame.title}
                className="h-10 w-auto rounded flex-shrink-0"
              />
            )}
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-sm font-medium truncate text-[rgba(6,7,21,1)] dark:text-white">
                {resolvedGame?.title ?? form.game_id}
              </span>
              <span className="text-xs opacity-60 truncate text-[rgba(6,7,21,1)] dark:text-white">{form.game_id}</span>
            </div>
            <button type="button" onClick={handleClearGame} className="btn btn-ghost btn-xs opacity-60 hover:opacity-100">
              ✕
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="text"
              placeholder={t("challenges.form.gameIdPlaceholder")}
              className="input input-bordered w-full"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => results.length > 0 && setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
            />
            {searchOpen && (
              <div className="absolute z-10 mt-1 left-0 right-0 max-h-64 overflow-auto rounded-lg border border-black/10 dark:border-white/20 bg-white dark:bg-[#0C0C1A] shadow-lg">
                {searching && <div className="px-3 py-2 text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">Searching…</div>}
                {!searching && results.length === 0 && query.trim().length >= 2 && (
                  <div className="px-3 py-2 text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">No matches.</div>
                )}
                {results.map((g) => (
                  <button
                    type="button"
                    key={g.identifier}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelectGame(g)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-[rgba(132,_0,_168,_0.06)] dark:hover:bg-[rgba(132,_0,_168,_0.1)] text-[rgba(6,7,21,1)] dark:text-white"
                  >
                    {(g.image_vertical || g.image) && (
                      <img src={g.image_vertical || g.image} alt={g.title} className="h-10 w-auto rounded flex-shrink-0" />
                    )}
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-sm">{g.title}</span>
                      <span className="text-xs opacity-60 truncate">{g.gameProvider?.title ?? g.provider}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {inputFields.map((field) => (
        <InputField
          key={field.name}
          name={field.name}
          title={field.title}
          placeholder={field.placeholder}
          type={field.type}
          value={field.value}
          onChange={handleChange}
          altLabelOne={field.altLabelOne}
        />
      ))}

      <label className="w-full form-control">
        <span className="label-text mb-1">{t("challenges.form.endDate")}</span>
        <input name="end_at" type="date" value={form.end_at} onChange={handleChange} className="input input-bordered w-full" />
      </label>

      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" className="checkbox checkbox-primary" checked={form.is_active} onChange={handleCheckbox} />
        <span>{t("challenges.form.active")}</span>
      </label>

      <div className="flex gap-2 justify-end mt-4">
        <button type="button" onClick={onCancel} className="btn btn-ghost">
          {t("challenges.form.cancel")}
        </button>
        <SaveButton onClick={handleSave} disabled={!isValid} loading={loading}>
          {t("challenges.form.save")}
        </SaveButton>
      </div>
    </div>
  )
}
